// web/src/features/rtls/rtlsService.ts
//
// Web RTLS data service — mirrors the mobile pattern at src/features/rtls/rtlsService.ts
// but uses the 'Stream' naming to reflect the postgres_changes subscription
// (mobile's historical 'Polling' name has no interval either; M2 web is
// the first build with an actual real-time subscription).
//
// Free-tier aware: postgres_changes only, no Broadcast. Budget estimate
// ~5K msgs/month for 1 user (0.25% of 2M cap).

import type { AppDispatch } from '../../app/store'
import { rtlsActions } from '@shared/slices/rtlsSlice'
import { activityActions } from '@shared/slices/activitySlice'
// RTLS_DB_CHANNEL is no longer used directly — each postgres_changes
// subscription now owns its own channel. Kept in the shared client module
// for any future broadcast-style usage.
import type {
  RTLSAsset,
  LocationData,
  LocationType,
  AccessPoint,
  AccessPointStatus,
} from '@shared/types/rtlsTypes'
import {
  mapAccessPointRow,
  mapChamberRow,
  mapEquipmentRowToAsset,
  mapPersonnelRowToAsset,
} from '@shared/supabase/assetMappers'
import { supabase } from '../../app/supabaseClient'

// Enum narrowing (same helpers as mobile — DB rows may drift from the union).
const ASSET_TYPES = new Set<string>(['equipment', 'personnel'])
const ASSET_STATUSES = new Set<string>(['active', 'idle', 'alert', 'offline'])
const ASSET_SUBTYPES = new Set<string>([
  'truck', 'loader', 'drill_rig', 'rescue_vehicle', 'other',
  'worker', 'supervisor', 'emergency_team',
])
const LOCATION_TYPES = new Set<string>([
  'entry', 'tunnel', 'station', 'safety', 'infrastructure', 'stope', 'surface',
])
const AP_STATUSES = new Set<string>(['active', 'offline', 'fault'])

function narrowAssetType(v: string): RTLSAsset['type'] | undefined {
  return ASSET_TYPES.has(v) ? (v as RTLSAsset['type']) : undefined
}
function narrowAssetStatus(v: string): RTLSAsset['status'] | undefined {
  return ASSET_STATUSES.has(v) ? (v as RTLSAsset['status']) : undefined
}
function narrowAssetSubType(v: string | null): RTLSAsset['subType'] | undefined {
  if (v == null) return undefined
  return ASSET_SUBTYPES.has(v) ? (v as RTLSAsset['subType']) : undefined
}
function narrowLocationType(v: string): LocationType | undefined {
  return LOCATION_TYPES.has(v) ? (v as LocationType) : undefined
}
function narrowAccessPointStatus(v: string): AccessPointStatus | undefined {
  return AP_STATUSES.has(v) ? (v as AccessPointStatus) : undefined
}

// Module-scoped channel refs for clean teardown across HMR/unmount.
//
// Architecture note: each postgres_changes subscription gets its OWN channel.
// Supabase realtime can silently drop individual table registrations when
// many `.on('postgres_changes', ...)` listeners share a single channel —
// SUBSCRIBED fires but specific tables never deliver events. One-channel-
// per-table is the canonical workaround and keeps registration failures
// visible per table (each channel reports its own SUBSCRIBED/CHANNEL_ERROR).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let _locationsChannel: any = null
let _personnelChannel: any = null
let _equipmentChannel: any = null
let _chambersChannel: any = null
let _accessPointsChannel: any = null
let _chamberPersonnelChannel: any = null
let _tagEventsChannel: any = null
let _locationEventsChannel: any = null

// ─── Supabase path — initial fetch + postgres_changes subscription ─────
async function startSupabasePath(dispatch: AppDispatch, mineId: string): Promise<void> {
  dispatch(rtlsActions.setConnectionStatus('reconnecting'))

  try {
    // All queries + subscriptions are scoped to the current mineId so the
    // free-tier budget stays linear in users (not users × mines) and local
    // state is never polluted by cross-mine rows.

    // ── Assets (roster + cached last-known position) ────────────────
    const { data: assetsRows, error: assetsErr } = await supabase
      .from('assets')
      .select('id, name, type, subtype, status, x, y, z, level, last_updated, external_id')
      .eq('mine_id', mineId)
    if (assetsErr) {
      throw new Error(`assets fetch failed: ${assetsErr.message} (code=${assetsErr.code ?? 'n/a'})`)
    }

    const assets: RTLSAsset[] = ((assetsRows ?? []) as any[])
      .map((r: any): RTLSAsset | null => {
        const type = narrowAssetType(r.type)
        const status = narrowAssetStatus(r.status)
        if (!type || !status) return null
        return {
          id: r.id,
          externalId: typeof r.external_id === 'string' ? r.external_id : undefined,
          name: r.name,
          type,
          subType: narrowAssetSubType(r.subtype),
          status,
          position: { x: r.x ?? 0, y: r.y ?? 0, z: r.z ?? 0 },
          lastUpdated: r.last_updated ? Date.parse(r.last_updated) : Date.now(),
          level: r.level ?? undefined,
        }
      })
      .filter((a: RTLSAsset | null): a is RTLSAsset => a !== null)
    dispatch(rtlsActions.updatePositions(assets))

    // ── Positions (last 7 days) → trail history ─────────────────────
    // Originally a 10-minute window (carried over from mobile where data
    // polls frequently). For v1 web with seeded demo data that can be
    // hours/days old, a 7-day window shows the seed trails AND picks up
    // any DB edits (row deletes in the positions table now reflect on
    // refresh). Still bounded so payload stays predictable.
    const trailWindowStart = new Date(
      Date.now() - 7 * 24 * 60 * 60 * 1000,
    ).toISOString()
    const { data: posRows, error: posErr } = await supabase
      .from('positions')
      .select('asset_id, x, y, z, timestamp')
      .gte('timestamp', trailWindowStart)
      .order('timestamp', { ascending: true })
    if (posErr) {
      throw new Error(`positions fetch failed: ${posErr.message} (code=${posErr.code ?? 'n/a'})`)
    }

    if (posRows) {
      const trails: Record<
        string,
        { position: { x: number; y: number; z: number }; timestamp: number }[]
      > = {}
      for (const r of posRows as any[]) {
        if (!trails[r.asset_id]) trails[r.asset_id] = []
        trails[r.asset_id].push({
          position: { x: r.x, y: r.y, z: r.z },
          timestamp: Date.parse(r.timestamp),
        })
      }
      dispatch(rtlsActions.loadTrailHistory(trails))
    }

    // ── Locations (all points of interest) ──────────────────────────
    const { data: locRows, error: locErr } = await supabase
      .from('locations')
      .select('id, name, type, x, y, z, level, mine_id')
      .eq('mine_id', mineId)
    if (locErr) {
      throw new Error(`locations fetch failed: ${locErr.message} (code=${locErr.code ?? 'n/a'})`)
    }

    const locations: LocationData[] = ((locRows ?? []) as any[])
      .map((r: any): LocationData | null => {
        // Fall back to 'station' when r.type is null — TagBoard form lets
        // operators save without picking a Type chip; we still render those.
        const narrowed = narrowLocationType(r.type)
        const type = narrowed ?? (r.type == null ? ('station' as const) : undefined)
        if (!type) return null
        return {
          id: r.id,
          name: r.name,
          type,
          position: { x: r.x ?? 0, y: r.y ?? 0, z: r.z ?? 0 },
          level: r.level ?? undefined,
          mine_id: r.mine_id ?? undefined,
        }
      })
      .filter((l: LocationData | null): l is LocationData => l !== null)
    dispatch(rtlsActions.setLocations(locations))

    // ── Access points (infrastructure — routers linked to locations) ──
    // Static read on page load only; no realtime subscription in v1.
    // A page reload picks up any operator-added / removed APs.
    const { data: apRows, error: apErr } = await supabase
      .from('access_points')
      .select('id, name, location_id, x, y, z, level, status, mac_address, radius_m, mine_id, created_at')
      .eq('mine_id', mineId)
    if (apErr) {
      throw new Error(`access_points fetch failed: ${apErr.message} (code=${apErr.code ?? 'n/a'})`)
    }

    const accessPoints: AccessPoint[] = ((apRows ?? []) as any[])
      .map((r: any): AccessPoint | null => {
        const status = narrowAccessPointStatus(r.status)
        if (!status) return null
        return {
          id: r.id,
          locationId: r.location_id ?? undefined,
          name: r.name,
          position: { x: r.x, y: r.y, z: r.z },
          level: r.level ?? undefined,
          status,
          macAddress: r.mac_address ?? undefined,
          radiusM: r.radius_m ?? undefined,
          mine_id: r.mine_id ?? undefined,
          createdAt: r.created_at ? Date.parse(r.created_at) : undefined,
        }
      })
      .filter((a: AccessPoint | null): a is AccessPoint => a !== null)
    dispatch(rtlsActions.setAccessPoints(accessPoints))

    // ── Rescue chambers — TagBoard-owned, RTLS-rendered as 3D markers ──
    const { data: chamberRows, error: chamberErr } = await (supabase as any)
      .from('rescue_chambers')
      .select(
        'id, mine_id, name, location, location_description, capacity, current_occupancy, ' +
        'status, x, y, z, level',
      )
      .eq('mine_id', mineId)
    if (chamberErr) {
      throw new Error(
        `rescue_chambers fetch failed: ${chamberErr.message} (code=${chamberErr.code ?? 'n/a'})`,
      )
    }
    const chambers = ((chamberRows ?? []) as any[])
      .map(mapChamberRow)
      .filter((c): c is NonNullable<typeof c> => c !== null)
    dispatch(rtlsActions.setRescueChambers(chambers))

    // ── Realtime subscription (postgres_changes) ─────────────────────
    // Single channel, multiple table subscriptions. The `assets` SQL view
    // does NOT support realtime — subscribe to the underlying personnel
    // and equipment tables, then map each UPDATE payload to an RTLSAsset
    // using the same status mapping the view applies server-side.
    //
    // The personnel/equipment UPDATE subs fire on TagBoard CRUD events
    // (tag-in/tag-out, status flips) and infrequent producer position
    // writes. They are NOT a 60Hz live-tracking path — that remains
    // paid-tier Broadcast territory.
    const mineFilter = `mine_id=eq.${mineId}`

    // Locations gets its own channel. Bundling many postgres_changes listeners
    // on a single channel can cause individual table registrations to fail
    // silently — SUBSCRIBED fires for the channel but specific tables don't
    // deliver events. Isolating locations is a known workaround.
    _locationsChannel = supabase
      .channel('rtls-db-locations')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'locations' },
        (payload: any) => {
          console.log('[RTLS][loc-ch] event:', payload.eventType, payload.new ?? payload.old)
          if (payload.eventType === 'DELETE') {
            if (payload.old?.id) dispatch(rtlsActions.removeLocation(payload.old.id))
            return
          }
          const r = payload.new
          if (r.mine_id && r.mine_id !== mineId) return
          const type = narrowLocationType(r.type) ?? (r.type == null ? 'station' as const : undefined)
          if (!type) {
            console.warn('[RTLS] locations event dropped — unknown type:', r.type, r)
            return
          }
          dispatch(rtlsActions.addLocation({
            id: r.id,
            name: r.name,
            type,
            position: { x: r.x ?? 0, y: r.y ?? 0, z: r.z ?? 0 },
            level: r.level ?? undefined,
            mine_id: r.mine_id ?? undefined,
          }))
        },
      )
      .subscribe((status: string, err?: any) => {
        console.log(`[RTLS] locations-channel status: ${status}`, err ?? '')
      })

    // ── Per-table channels. Each gets its own subscription so registration
    //    failures are isolated and visible per table. ──────────────────────

    // Personnel: all events. INSERT lands new workers in 3D; DELETE removes orphans.
    _personnelChannel = supabase
      .channel('rtls-db-personnel')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'personnel', filter: mineFilter },
        (payload: any) => {
          console.log('[RTLS][personnel-ch] event:', payload.eventType, payload.new?.id ?? payload.old?.id)
          if (payload.eventType === 'DELETE') {
            if (payload.old?.id) dispatch(rtlsActions.removeAsset(payload.old.id))
            return
          }
          const asset = mapPersonnelRowToAsset(payload.new)
          if (asset) dispatch(rtlsActions.updateSingleAsset(asset))
        },
      )
      .subscribe((status: string, err?: any) => {
        console.log(`[RTLS] personnel-channel status: ${status}`, err ?? '')
        if (status === 'SUBSCRIBED') {
          dispatch(rtlsActions.setConnectionStatus('connected'))
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          dispatch(rtlsActions.setConnectionStatus('reconnecting'))
        } else if (status === 'CLOSED') {
          dispatch(rtlsActions.setConnectionStatus('disconnected'))
        }
      })

    // Equipment: all events. Same INSERT/DELETE coverage as personnel.
    _equipmentChannel = supabase
      .channel('rtls-db-equipment')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'equipment', filter: mineFilter },
        (payload: any) => {
          console.log('[RTLS][equipment-ch] event:', payload.eventType, payload.new?.id ?? payload.old?.id)
          if (payload.eventType === 'DELETE') {
            if (payload.old?.id) dispatch(rtlsActions.removeAsset(payload.old.id))
            return
          }
          const asset = mapEquipmentRowToAsset(payload.new)
          if (asset) dispatch(rtlsActions.updateSingleAsset(asset))
        },
      )
      .subscribe((status: string, err?: any) => {
        console.log(`[RTLS] equipment-channel status: ${status}`, err ?? '')
      })

    // Rescue chambers: any event — recolor markers + update occupancy.
    _chambersChannel = supabase
      .channel('rtls-db-chambers')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'rescue_chambers', filter: mineFilter },
        (payload: any) => {
          console.log('[RTLS][chambers-ch] event:', payload.eventType, payload.new?.id ?? payload.old?.id)
          if (payload.eventType === 'DELETE') {
            if (payload.old?.id) dispatch(rtlsActions.removeRescueChamber(payload.old.id))
            return
          }
          const chamber = mapChamberRow(payload.new)
          if (chamber) dispatch(rtlsActions.upsertRescueChamber(chamber))
        },
      )
      .subscribe((status: string, err?: any) => {
        console.log(`[RTLS] chambers-channel status: ${status}`, err ?? '')
      })

    // Access points: any event — TagBoard CRUD or status flips.
    _accessPointsChannel = supabase
      .channel('rtls-db-access-points')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'access_points', filter: mineFilter },
        (payload: any) => {
          console.log('[RTLS][ap-ch] event:', payload.eventType, payload.new?.id ?? payload.old?.id)
          if (payload.eventType === 'DELETE') {
            if (payload.old?.id) dispatch(rtlsActions.removeAccessPoint(payload.old.id))
            return
          }
          const ap = mapAccessPointRow(payload.new)
          if (ap) dispatch(rtlsActions.upsertAccessPoint(ap))
        },
      )
      .subscribe((status: string, err?: any) => {
        console.log(`[RTLS] access-points-channel status: ${status}`, err ?? '')
      })

    // chamber_personnel: INSERT/DELETE bundled on one channel (same table).
    // Live occupancy adjustment so 3D marker capacity bars stay accurate.
    _chamberPersonnelChannel = supabase
      .channel('rtls-db-chamber-personnel')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'chamber_personnel' },
        (payload: any) => {
          console.log('[RTLS][cp-ch] INSERT:', payload.new?.chamber_id)
          if (payload.new?.chamber_id) {
            dispatch(rtlsActions.incrementChamberOccupancy({
              chamberId: payload.new.chamber_id,
              delta: 1,
            }))
          }
        },
      )
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'chamber_personnel' },
        (payload: any) => {
          console.log('[RTLS][cp-ch] DELETE:', payload.old?.chamber_id)
          if (payload.old?.chamber_id) {
            dispatch(rtlsActions.incrementChamberOccupancy({
              chamberId: payload.old.chamber_id,
              delta: -1,
            }))
          }
        },
      )
      .subscribe((status: string, err?: any) => {
        console.log(`[RTLS] chamber-personnel-channel status: ${status}`, err ?? '')
      })

    // tag_events: append-only INSERT feed for the activity panel.
    _tagEventsChannel = supabase
      .channel('rtls-db-tag-events')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'tag_events' },
        (payload: any) => {
          console.log('[RTLS][tag-events-ch] INSERT:', payload.new?.id)
          const r = payload.new
          if (!r) return
          dispatch(activityActions.prependTagEvent({
            id: r.id,
            tag_id: r.tag_id,
            personnel_id: r.personnel_id ?? null,
            equipment_id: r.equipment_id ?? null,
            type: r.type === 'IN' ? 'IN' : 'OUT',
            shift_id: r.shift_id ?? null,
            timestamp: r.timestamp,
          }))
        },
      )
      .subscribe((status: string, err?: any) => {
        console.log(`[RTLS] tag-events-channel status: ${status}`, err ?? '')
      })

    // location_events: append-only zone-entry feed.
    _locationEventsChannel = supabase
      .channel('rtls-db-location-events')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'location_events' },
        (payload: any) => {
          console.log('[RTLS][loc-events-ch] INSERT:', payload.new?.id)
          const r = payload.new
          if (!r) return
          dispatch(activityActions.prependLocationEvent({
            id: r.id,
            location_id: r.location_id,
            access_point_id: r.access_point_id ?? null,
            personnel_id: r.personnel_id,
            event_type: r.event_type === 'entry_in' ? 'entry_in' : 'entry_out',
            timestamp: r.timestamp,
          }))
        },
      )
      .subscribe((status: string, err?: any) => {
        console.log(`[RTLS] location-events-channel status: ${status}`, err ?? '')
      })

    console.log(
      `[RTLS] Initial load complete. ` +
      `Assets: ${assets.length}, locations: ${locations.length}, ` +
      `access points: ${accessPoints.length}, chambers: ${chambers.length}, ` +
      `trail points: ${posRows?.length ?? 0}. ` +
      `7 per-table channels subscribed.`,
    )
  } catch (err: unknown) {
    console.error('[RTLS] Supabase initial load failed:', err)
    dispatch(rtlsActions.setConnectionStatus('disconnected'))
  }
}

// ─── Public API ─────────────────────────────────────────────────────────
/**
 * Starts the RTLS data flow.
 *
 * @param dispatch - Redux dispatch
 * @param mineId   - Current mine UUID. Required — SELECT + postgres_changes
 *                   subscriptions are scoped by mine_id. If null, the stream
 *                   enters 'disconnected' state and no data loads.
 */
export function startRTLSStream(dispatch: AppDispatch, mineId: string | null): void {
  stopRTLSStream()
  if (!mineId) {
    console.error('[RTLS] Cannot start stream: mineId is null. Check useBootstrapMap.')
    dispatch(rtlsActions.setConnectionStatus('disconnected'))
    return
  }
  void startSupabasePath(dispatch, mineId)
}

export function stopRTLSStream(): void {
  // Tear down every per-table channel. Each `if` block guards against a
  // partial start (e.g. error mid-initial-fetch leaving some channels unset).
  const channels = [
    ['locations', _locationsChannel],
    ['personnel', _personnelChannel],
    ['equipment', _equipmentChannel],
    ['chambers', _chambersChannel],
    ['access-points', _accessPointsChannel],
    ['chamber-personnel', _chamberPersonnelChannel],
    ['tag-events', _tagEventsChannel],
    ['location-events', _locationEventsChannel],
  ] as const
  for (const [, ch] of channels) {
    if (ch) supabase.removeChannel(ch)
  }
  _locationsChannel = null
  _personnelChannel = null
  _equipmentChannel = null
  _chambersChannel = null
  _accessPointsChannel = null
  _chamberPersonnelChannel = null
  _tagEventsChannel = null
  _locationEventsChannel = null
}

// HMR-safe teardown: if Vite swaps this module, tear down the old channel
// before the new one gets created. Prevents a slow leak during dev reloads.
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    stopRTLSStream()
  })
}
